import { useState } from 'react';
import { Switch } from '@/components/forms/Switch';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { useAuthenticatorSetup, useDisableTwoFactor, useEnableAuthenticator, useSecurityStatus } from './accountSecurity';

// Two-Factor Authentication (right column of the legacy Security tab): a switch that
// reveals the authenticator setup (shared key + QR + verification code) when enabling,
// or a verification-code input when disabling. Mirrors profile-account.js.
export function TwoFactorPanel() {
  const toast = useToast();
  const status = useSecurityStatus();
  const enabled = status.data?.twoFactorEnabled ?? false;
  const setup = useAuthenticatorSetup(!enabled); // only fetch setup when 2FA is off
  const enableMut = useEnableAuthenticator();
  const disableMut = useDisableTwoFactor();

  const [enableCode, setEnableCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showQr, setShowQr] = useState(false);

  const busy = enableMut.isPending || disableMut.isPending;

  const onToggle = async (next: boolean) => {
    if (busy) return;
    if (next) {
      if (!/^\d{6}$/.test(enableCode)) {
        toast.error('Enter the 6-digit verification code from your authenticator app.');
        return;
      }
      try {
        await enableMut.mutateAsync({ verificationCode: enableCode });
        toast.success('Two-factor authentication enabled.');
        setEnableCode('');
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Failed to enable two-factor authentication.');
      }
    } else {
      if (!/^\d{6}$/.test(disableCode)) {
        toast.error('Enter the 6-digit verification code to disable.');
        return;
      }
      try {
        await disableMut.mutateAsync({ verificationCode: disableCode });
        toast.success('Two-factor authentication disabled.');
        setDisableCode('');
        setShowQr(false);
        void setup.refetch();
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Failed to disable two-factor authentication.');
      }
    }
  };

  const copyKey = async () => {
    const key = setup.data?.sharedKey;
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      toast.info('Shared key copied.');
    } catch {
      toast.error('Could not copy the shared key.');
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-1">
        <h6 className="fw-semibold mb-0">Two-Factor Authentication</h6>
        <Switch checked={enabled} disabled={busy} onChange={onToggle} />
      </div>
      <p className="text-muted fs-13">Add an extra layer of security using an authenticator app.</p>

      {!enabled ? (
        <div>
          <label className="form-label mb-1">Shared Key</label>
          <div className="d-flex align-items-center gap-2 mb-2">
            <code className="flex-grow-1 text-break">{setup.data?.sharedKey ?? '…'}</code>
            <button type="button" className="btn btn-sm btn-light" title="Copy" onClick={copyKey}><i className="fa fa-copy" /></button>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowQr((v) => !v)}>
              <i className="fa fa-qrcode me-1" />{showQr ? 'Hide QR' : 'Show QR'}
            </button>
          </div>
          {showQr && setup.data?.qrCodeDataUrl && (
            <div className="mb-2"><img src={setup.data.qrCodeDataUrl} alt="Authenticator QR code" style={{ width: 160, height: 160 }} /></div>
          )}
          <label className="form-label mb-1">Verification Code</label>
          <input
            className="form-control"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={enableCode}
            onChange={(e) => setEnableCode(e.target.value.replace(/\D/g, ''))}
          />
          <div className="form-text">Turn the switch on after entering the code to enable.</div>
        </div>
      ) : (
        <div>
          <label className="form-label mb-1">Verification Code</label>
          <input
            className="form-control"
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
          />
          <div className="form-text">Turn the switch off after entering the code to disable.</div>
        </div>
      )}
    </div>
  );
}
